// Új cikk. Két döntés, semmi több: hova kerüljön és mi a címe — a tartalom a szerkesztőben jön.
// Egy üres űrlap harminc mezővel elriaszt; itt a cél az, hogy két kattintás után már írni lehessen.
import { PERMISSIONS } from '@morgens-hub/shared';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { AppShell } from '@/components/shared/app-shell';
import { KnowledgeCrumbs } from '@/components/shared/knowledge-crumbs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useCreateArticle, useStructure, useTemplates } from '@/features/editor/api';
import { ArticleLocationPicker } from '@/features/knowledge/components/article-location-picker';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { useErrorText } from '@/lib/use-error-text';
import { useHasPermission } from '@/lib/use-permission';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/uj-cikk')({
  // A könyv-kontextusból induló cikk-létrehozás (`/hub/$spaceSlug`, a könyv-sor „Cikk létrehozása"
  // művelete) ide navigál a szekció/könyv azonosítójával — enélkül a felhasználónak ITT kellene
  // újra kiválasztania mindkettőt, amit az előző oldalon már megtett. Opcionális: a fejléc-linkről
  // közvetlenül idelépve nincs preset, és a lap enélkül is működik (első elem alapból kiválasztva).
  //
  // A `title` a HIÁNYNAPLÓBÓL jön (Karbantartás → „Cikk írása erről"): a hiába keresett kifejezés
  // lesz a cím kiindulópontja. Enélkül a szerkesztőnek vissza kellene lapoznia és bemásolnia —
  // pontosan az a súrlódás, amitől a hiánynapló megmarad puszta listának.
  validateSearch: (
    search: Record<string, unknown>,
  ): { spaceId?: string; categoryId?: string; title?: string } => ({
    ...(typeof search.spaceId === 'string' && search.spaceId ? { spaceId: search.spaceId } : {}),
    ...(typeof search.categoryId === 'string' && search.categoryId ? { categoryId: search.categoryId } : {}),
    ...(typeof search.title === 'string' && search.title ? { title: search.title.slice(0, 200) } : {}),
  }),
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: NewArticlePage,
});

function NewArticlePage(): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const navigate = useNavigate();

  const session = api.session.useQuery(['session']);
  const structure = useStructure();
  const create = useCreateArticle();
  // A CIKK-VÁZAK. A választás lokális állapot: `null` = üres cikk (a mai viselkedés, alapértelmezés).
  const templates = useTemplates('article');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const canManageStructure = useHasPermission(PERMISSIONS.categoryManage);
  const canManageSpaces = useHasPermission(PERMISSIONS.spaceManage);
  const {
    spaceId: presetSpaceId,
    categoryId: presetCategoryId,
    title: presetTitle,
  } = Route.useSearch();

  const [spaceId, setSpaceId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  // A kiinduló cím a hiánynaplóból érkezhet; a mező innentől a szerkesztőé (nem írjuk felül újra).
  const [title, setTitle] = useState(presetTitle ?? '');

  const sessionBody = session.data?.status === 200 ? session.data.body : null;
  const spaces = useMemo(() => {
    const body = structure.data?.status === 200 ? structure.data.body : null;
    return (body?.spaces ?? []).filter((s) => !s.archived);
  }, [structure.data]);

  // Az első terület alapból kiválasztva — a leggyakoribb eset egy kattintással kevesebb. HA a hívó
  // hozott egy `spaceId`-t (a könyv-kontextusból induló link), és az MÉG szerepel a listában
  // (nem archiválták időközben), azt választjuk — a preset a hívó SZÁNDÉKA, nem csak egy alapérték.
  useEffect(() => {
    if (spaceId || !spaces.length) return;
    const preset = presetSpaceId && spaces.some((s) => s.id === presetSpaceId) ? presetSpaceId : spaces[0]!.id;
    setSpaceId(preset);
  }, [spaces, spaceId, presetSpaceId]);

  const categories = useMemo(
    () => spaces.find((s) => s.id === spaceId)?.categories.filter((c) => !c.archived) ?? [],
    [spaces, spaceId],
  );
  // Ugyanez a könyvre: a `presetCategoryId` CSAK akkor érvényesül, ha a KIVÁLASZTOTT szekció
  // könyvei közt szerepel — ha a hívó rossz párost adna (más szekció könyve), csendben az adott
  // szekció első könyvére esünk vissza, nem egy nem-létező/idegen azonosítóra.
  useEffect(() => {
    setCategoryId((current) => {
      // A MÁR KIVÁLASZTOTT könyv marad, ha még létezik. Enélkül minden szerkezet-újratöltés (pl. egy
      // frissen létrehozott könyv után) visszaugrasztotta a választást a lista első elemére — épp
      // azt dobva el, amit a felhasználó egy másodperccel korábban létrehozott.
      if (current && categories.some((c) => c.id === current)) return current;
      if (presetCategoryId && categories.some((c) => c.id === presetCategoryId)) {
        return presetCategoryId;
      }
      return categories[0]?.id ?? '';
    });
  }, [categories, presetCategoryId]);

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!categoryId || !title.trim()) return;

    const chosen = templates.data?.body.templates.find((x) => x.id === templateId);
    create.mutate(
      {
        body: {
          categoryId,
          title: title.trim(),
          // A váz törzse a LÉTREHOZÁSSAL EGYÜTT megy — nem egy második mentés-körrel. Enélkül egy
          // megszakadt folyamat (bezárt lap, hálózati hiba) üres cikket hagyna a tudástárban.
          ...(chosen ? { body: chosen.body } : {}),
        },
      },
      {
        onSuccess: (response) => {
          toast.success(t('articleCreated', { ns: 'toast' }));
          void navigate({
            to: '/szerkesztes/$articleId',
            params: { articleId: response.body.id },
          });
        },
        onError: (failure: unknown) => {
          toast.error(errorText(failure, t('newArticle.failedOperation')));
        },
      },
    );
  };

  return (
    <AppShell session={sessionBody}>
      <div className="mx-auto max-w-xl">
        <KnowledgeCrumbs current={t('newArticle.title')} />
        <h1 className="text-read-2xl font-semibold tracking-tight">{t('newArticle.title')}</h1>
        <p className="text-muted-foreground text-read-sm mt-2">{t('newArticle.subtitle')}</p>

        {structure.isLoading && <Skeleton className="mt-8 h-48 w-full" />}

        {!structure.isLoading && (
          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            {/* Hova kerüljön: szekció → könyv, a hierarchia szerint behúzva, helyben létrehozással.
                A választók és a létrehozó mezők egy komponensben élnek (`ArticleLocationPicker`),
                mert egyetlen állapotgép: mindig EGY sor van létrehozás alatt. */}
            <ArticleLocationPicker
              spaces={spaces}
              spaceId={spaceId}
              onSpaceChange={setSpaceId}
              categories={categories}
              categoryId={categoryId}
              onCategoryChange={setCategoryId}
              canManageSpaces={canManageSpaces}
              canManageCategories={canManageStructure}
            />

            {spaces.length > 0 && (
              <>
                <div>
                  <Label htmlFor="new-title">{t('newArticle.fieldTitle')}</Label>
                  <Input
                    id="new-title"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                    }}
                    placeholder={t('newArticle.titlePlaceholder')}
                    className="mt-1.5 h-11 text-base"
                    maxLength={300}
                    autoFocus
                    required
                  />
                  <p className="text-subtle mt-2 text-sm">{t('newArticle.hint')}</p>
                </div>

                {/* VÁZ-VÁLASZTÓ. Csak akkor jelenik meg, ha van mit választani — üres készlettel egy
                    „Üres cikk" feliratú magányos kártya értelmetlen zaj lenne. A választás
                    kölcsönösen kizáró, ezért `radiogroup` (ugyanaz a minta, mint a mód-váltónál). */}
                {templates.data && templates.data.body.templates.length > 0 && (
                  <div>
                    <span className="text-md font-medium">{t('templates.pickTitle')}</span>
                    <div role="radiogroup" aria-label={t('templates.pickTitle')} className="mt-2 grid gap-2 sm:grid-cols-2">
                      {[null, ...templates.data.body.templates.map((x) => x.id)].map((id) => {
                        const template = templates.data.body.templates.find((x) => x.id === id);
                        const active = templateId === id;
                        return (
                          <button
                            key={id ?? 'empty'}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => {
                              setTemplateId(id);
                            }}
                            className={cn(
                              'border-border ease-hub rounded-lg border p-3 text-left transition-colors duration-150',
                              active ? 'border-primary bg-primary/5' : 'hover:bg-accent',
                            )}
                          >
                            <span className="block font-medium">
                              {template ? template.name : t('templates.pickEmpty')}
                            </span>
                            <span className="text-subtle text-md block">
                              {template ? template.description : t('templates.pickEmptyHint')}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-1">
                  <Button
                    type="submit"
                    disabled={create.isPending || !categoryId || !title.trim()}
                  >
                    {create.isPending ? t('newArticle.creating') : t('newArticle.create')}
                  </Button>
                  <Button asChild type="button" variant="ghost">
                    <Link to="/">{t('editor.cancel')}</Link>
                  </Button>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </AppShell>
  );
}
